/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Spacings, Fonts } from '@styles/index';
import P2 from '@base/P2/P2';
import { IConcept, IConceptTopicGroup } from 'types/notes';
import P1 from '@base/P1/P1';
import Book from '@icon/Book';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import useFetch from '@hooks/useFetch';
import { useRouter } from 'next/router';
import { SYLLABUS_CLICKED_KEYS } from '@constants/segment';
import { segment } from '@actions/analytics';
import { evtNameCategoryMap } from '@constants/index';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useRequestStatusStore from '@stores/requestStatus';
import {
  getUberLoaderVisibility,
  getUberLoaderVisibilityUpdater
} from '@stores/selectors/requestStatus';
import KeyboardArrowDown from '@icon/notes/KeyboardArrowDown';
import { localStorageAvailable } from '@utils/localStorage';
import TopicsDropdown from './TopicsDropdown';
import {
  EPUB_NOTES,
  findFirstEnabledConcept,
  getConceptListEndpoint,
  NOTES_CONCEPT_SELECTED,
  NOTES_SUB_ITEM_SELECTED
} from '../utils';
import { EpubContext } from '../Context/EpubContext';
import SubItemsSkeleton from './SubItemsSkeleton';

interface ISideNav {
  children: IConcept[];
  name: string;
  parent?: IConceptTopicGroup;
}

interface ISideNavProps {
  topicUID: string;
  showNavigation: boolean;
  onChangeTopic: (uid: string) => void;
  setShowPDFViewer: React.Dispatch<any>;
  conceptPDFInfo: {};
  selectedSubItemIds: {};
  setSelectedSubItemIds: React.Dispatch<any>;
}

interface IConceptProps {
  $selected: boolean;
  $disabled: boolean;
}

interface ISubItemsProps {
  $selected: boolean;
}

interface IWrapperProps {
  $hide: boolean;
}

const Wrapper = styled.div<IWrapperProps>`
  height: 100%;
  width: ${({ $hide }) => ($hide ? '0' : '320px')};
  position: relative;
  left: ${({ $hide }) => ($hide ? '-100px' : '0')};
  transition: left 300ms ease-in-out, opacity 300ms ease-in-out,
    width 300ms ease-in-out;
  opacity: ${({ $hide }) => ($hide ? 0 : 1)};
  flex-direction: column;
  padding-right: ${({ $hide }) => ($hide ? 0 : `${Spacings.SPACING_7B}`)};
  user-select: none;
`;

const StyP2 = styled(P2)`
  font-weight: ${Fonts.LIGHT};
  line-height: 18px;
`;

const ConceptsList = styled.div`
  margin-top: ${Spacings.SPACING_6B};
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_2};
  overflow: hidden;
  max-height: calc(100vh - 182px);
  padding-right: ${Spacings.SPACING_4B};
  &::-webkit-scrollbar {
    width: ${Spacings.SPACING_1B};
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--color-text-secondary);
    border-radius: ${Spacings.SPACING_2B};
  }
  &:hover {
    overflow-y: scroll;
  }
`;

const Concept = styled.div<IConceptProps>`
  padding-top: ${Spacings.SPACING_3B};
  padding-bottom: ${Spacings.SPACING_3B};
  padding-left: ${Spacings.SPACING_4B};
  padding-right: ${({ $disabled }) => ($disabled ? '0' : Spacings.SPACING_4B)};
  display: flex;
  align-items: flex-start;
  gap: ${Spacings.SPACING_3B};
  border-radius: ${Spacings.SPACING_2B};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  background-color: ${({ $selected }) =>
    $selected ? 'var(--color-base-1)' : 'none'};
  box-shadow: ${({ $selected }) =>
    $selected ? boxShadowGenerator({ color: 'var(--color-shadow)' }) : 'none'};
  &:hover {
    background-color: ${({ $disabled }) =>
      $disabled ? 'none' : 'var(--color-base-1)'};
    box-shadow: ${({ $disabled }) =>
      $disabled
        ? 'none'
        : boxShadowGenerator({ color: 'var(--color-shadow)' })};
  }
`;

const StyP1 = styled(P1)<IConceptProps>`
  line-height: ${({ $selected }) => ($selected ? '18px' : Spacings.SPACING_5B)};
  font-weight: ${Fonts.SEMIBOLD};
  max-width: ${({ $disabled }) => ($disabled ? '178px' : 'auto')};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const IconWrapper = styled.div`
  height: ${Spacings.SPACING_4B};
  width: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_2};
  margin-bottom: ${Spacings.SPACING_2};
`;

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const SoonTag = styled.div`
  text-transform: uppercase;
  font-weight: ${Fonts.HEAVY};
  font-size: ${Fonts.EXTRA_SMALL};
  line-height: 10px;
  padding: ${Spacings.SPACING_2} ${Spacings.SPACING_6};
  color: var(--color-text-secondary);
  background-color: var(--color-divider);
  border-radius: ${Spacings.SPACING_1B};
  height: 14px;
  min-width: 42px;
`;

const InnerWrapper = styled.div`
  width: 320px;
`;

const SubItemsContainer = styled.div`
  padding: 0 ${Spacings.SPACING_4B} 0 ${Spacings.SPACING_11B};
`;

const SubItems = styled.div<ISubItemsProps>`
  padding: 9px 0;
  font-size: ${Fonts.SMALL};
  font-weight: ${({ $selected }) => ($selected ? Fonts.BOLD : Fonts.LIGHT)};
  color: var(--color-text-primary);
  &:hover {
    font-weight: ${Fonts.BOLD};
    cursor: pointer;
  }
`;

const StyIcon = styled(KeyboardArrowDown)`
  cursor: pointer;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 400ms ease-in-out;
`;

const SideNav: React.FC<ISideNavProps> = ({
  topicUID,
  showNavigation,
  onChangeTopic,
  setShowPDFViewer,
  conceptPDFInfo,
  selectedSubItemIds,
  setSelectedSubItemIds
}) => {
  const {
    query: { goalUID }
  } = useRouter();
  const { data = {}, isLoading } = useFetch(getConceptListEndpoint(topicUID));
  const { currentNotesUID, selectedConcept, onChangeSelectedConcept } =
    useContext(EpubContext);

  const showUberLoader = useRequestStatusStore(getUberLoaderVisibility);
  const setShowUberLoader = useRequestStatusStore(
    getUberLoaderVisibilityUpdater
  );

  useEffect(() => {
    if (isLoading) {
      setShowUberLoader(true);
    } else {
      setShowUberLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const { name, children = [], parent, uid } = data;
  const { name: topicGroupName, children: topics = [] } = parent || {};
  const goalSegmentData = useGoalEventProps({ goalUID: goalUID as string });
  const [expandedItem, setExpandedItem] = useState(null);

  const handlePDFAndEpubView = (concept) => {
    if (concept?.isEpubAvailable) {
      setShowPDFViewer(false);
      onChangeSelectedConcept(concept);
    } else if (concept.isNotesAvailable) {
      setShowPDFViewer(true);
      onChangeSelectedConcept(concept);
      if (expandedItem === concept.uid) {
        setExpandedItem(null);
      } else {
        setExpandedItem(concept.uid);
      }
    }
  };

  useEffect(() => {
    if (!isLoading && !!data) {
      const conceptDetails = findFirstEnabledConcept(children);
      handlePDFAndEpubView(conceptDetails);
    }
  }, [topicUID, isLoading]);

  useEffect(() => {
    if (!isLoading && !!data && selectedConcept?.uid) {
      setExpandedItem(selectedConcept.uid);
    }
  }, [selectedConcept?.uid]);

  const onClickConcept = (concept) => () => {
    let notesSegmentData = {};
    if (localStorageAvailable()) {
      notesSegmentData = JSON.parse(localStorage.getItem('notesSegmentData'));
    }
    segment.track(
      NOTES_CONCEPT_SELECTED,
      {
        ...goalSegmentData,
        ...notesSegmentData,
        last_primary_source: EPUB_NOTES,
        last_primary_source_section: EPUB_NOTES,
        notes_sub_topic: topicGroupName,
        notes_title: concept.name,
        notes_uid: currentNotesUID
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      SYLLABUS_CLICKED_KEYS
    );
    handlePDFAndEpubView(concept);
  };

  const onClickSubItems = (e, concept, item) => {
    e.stopPropagation();
    const { name, noteId } = item ?? {};
    segment.track(
      NOTES_SUB_ITEM_SELECTED,
      {
        ...goalSegmentData,
        last_primary_source: EPUB_NOTES,
        last_primary_source_section: EPUB_NOTES,
        notes_sub_topic: topicGroupName,
        notes_title: name,
        notes_uid: noteId
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      SYLLABUS_CLICKED_KEYS
    );
    setSelectedSubItemIds({ ...selectedSubItemIds, [concept.uid]: noteId });
    onChangeSelectedConcept(concept);
  };

  return (
    <Wrapper $hide={!showNavigation}>
      <InnerWrapper>
        <StyP2 color="var(--color-text-secondary)">{topicGroupName}</StyP2>
        {!isLoading ? (
          <TopicsDropdown
            label={name}
            value={uid}
            topics={topics}
            onChangeTopic={onChangeTopic}
          />
        ) : null}
      </InnerWrapper>

      <ConceptsList>
        {children.map((concept) => {
          const selected = selectedConcept?.uid === concept.uid;
          const disabled =
            !concept.isEpubAvailable && !concept.isNotesAvailable;
          const showSubChildren =
            !concept.isEpubAvailable &&
            concept.isNotesAvailable &&
            concept.filesCount > 1;
          const subChildren = showSubChildren
            ? conceptPDFInfo?.[concept.uid] || []
            : [];

          return (
            <>
              <Concept
                key={concept.uid}
                $selected={selected}
                $disabled={disabled}
                // @ts-ignore
                onClick={onClickConcept(concept, subChildren[0])}
              >
                <IconWrapper>
                  <Book
                    color={
                      disabled
                        ? 'var(--color-text-secondary)'
                        : 'var(--color-text-primary)'
                    }
                    // @ts-ignore
                    height={Spacings.SPACING_4B}
                    width={Spacings.SPACING_4B}
                  />
                </IconWrapper>
                <InfoWrapper>
                  <StyP1
                    $selected={selected}
                    $disabled={disabled}
                    color={
                      disabled
                        ? 'var(--color-text-secondary)'
                        : 'var(--color-text-primary)'
                    }
                  >
                    {concept.name}
                  </StyP1>
                  {disabled ? <SoonTag>Soon</SoonTag> : null}
                </InfoWrapper>
                {showSubChildren ? (
                  <StyIcon
                    color="var(--color-text-primary)"
                    height={Spacings.SPACING_6B}
                    width={Spacings.SPACING_6B}
                    $open={concept.uid === expandedItem}
                  />
                ) : null}
              </Concept>
              {showSubChildren && selected && concept.uid === expandedItem ? (
                <SubItemsContainer>
                  {!subChildren.length ? (
                    <SubItemsSkeleton />
                  ) : (
                    subChildren.map((item) => (
                      <SubItems
                        key={item.noteId}
                        $selected={
                          selectedSubItemIds?.[concept.uid] === item.noteId
                        }
                        onClick={(e) => onClickSubItems(e, concept, item)}
                      >
                        {item.name}
                      </SubItems>
                    ))
                  )}
                </SubItemsContainer>
              ) : null}
            </>
          );
        })}
      </ConceptsList>
    </Wrapper>
  );
};

export default SideNav;
